import { buildLetter, buildNextSteps, grade10Flag, missingForNotice } from "../shared/legal/notice";
import type { NoticeInput } from "../shared/legal/notice";
import { GRADES, subjectsFor } from "../shared/types";
import type { Child, Enrollment, Grade, Intake } from "../shared/types";
import { acknowledge, byId, h, replace, toast } from "./lib/dom";

const EXAMPLE_START_IN_DAYS = 7;

const form = byId<HTMLFormElement>("f");
const out = byId("out");
const gradeSelect = byId<HTMLSelectElement>("cgrade");

function field(id: string): string {
  return byId<HTMLInputElement | HTMLSelectElement>(id).value.trim();
}

function setField(id: string, value: string): void {
  byId<HTMLInputElement | HTMLSelectElement>(id).value = value;
}

function readGrade(): Grade | "" {
  return GRADES.find((grade) => grade === gradeSelect.value) ?? "";
}

/** The second choice covers private school, out of state, and never enrolled. All three take the same branch. */
function readEnrollment(): Enrollment {
  const checked = form.querySelector<HTMLInputElement>('input[name="enrolled"]:checked');
  return checked?.value === "private" ? "private" : "hi-public";
}

function readNotice(): NoticeInput {
  const legalName = field("cname");
  const child: Child = {
    id: "child",
    firstName: legalName.split(/\s+/)[0] ?? "",
    legalName,
    birthdate: field("cdob"),
    grade: readGrade(),
    interests: "",
  };
  const intake: Intake = {
    parentName: field("pname"),
    parentEmail: "",
    parentPhone: field("cphone"),
    address: field("caddr"),
    children: [child],
    enrollment: readEnrollment(),
    schoolOfRecord: field("school"),
    principal: field("principal"),
    startDate: field("start"),
    whyNow: "",
    worry: "",
  };
  return { child, intake, today: Date.now() };
}

function sentenceList(items: readonly string[]): string {
  if (items.length <= 1) return items.join("");
  if (items.length === 2) return items.join(" and ");
  return `${items.slice(0, -1).join(", ")}, and ${items.at(-1)}`;
}

function renderMissing(notice: NoticeInput): void {
  const note = byId("missing");
  const missing = missingForNotice(notice).map((name) => name.replace("'", "\u2019"));
  note.hidden = missing.length === 0;
  replace(note, missing.length > 0 && h("p", {}, `Still blank in your letter: ${sentenceList(missing)}.`));
}

function renderNextSteps(notice: NoticeInput): void {
  replace(
    byId("nextlist"),
    ...buildNextSteps(notice).map((step) =>
      h("li", {}, h("strong", {}, step.title), h("p", { class: "muted small" }, step.detail)),
    ),
  );
}

function renderGrade10Flag(grade: Grade | ""): void {
  const flag = byId("g10flag");
  const text = grade10Flag(grade);
  flag.hidden = text === null;
  if (text === null) {
    replace(flag);
    return;
  }
  const firstSentenceEnd = text.indexOf(". ") + 1;
  replace(flag, h("p", {}, h("strong", {}, text.slice(0, firstSentenceEnd)), text.slice(firstSentenceEnd)));
}

function render(scrollIntoView: boolean): void {
  const notice = readNotice();
  byId("letter").textContent = buildLetter(notice);
  renderMissing(notice);
  renderNextSteps(notice);
  byId("subjrow").textContent = subjectsFor(notice.child.grade).join(", ");
  renderGrade10Flag(notice.child.grade);

  const wasHidden = out.hidden;
  out.hidden = false;
  if (scrollIntoView && wasHidden) out.scrollIntoView({ behavior: "smooth", block: "start" });
}

function localIsoDate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function fillExample(): void {
  const start = new Date();
  start.setDate(start.getDate() + EXAMPLE_START_IN_DAYS);
  setField("cname", "Kai J. Nakamura");
  setField("cdob", "2016-04-11");
  setField("cgrade", "Grade 4");
  setField("caddr", "45 Kalaheo Ave, Kailua, HI 96734");
  setField("cphone", "808-555-0142");
  setField("pname", "Leilani Nakamura");
  setField("school", "Maunawili Elementary School");
  setField("principal", "");
  setField("start", localIsoDate(start));
  render(true);
}

async function copyLetter(): Promise<void> {
  try {
    await navigator.clipboard.writeText(byId("letter").textContent ?? "");
    acknowledge(byId("copy"), "Copied");
  } catch {
    toast("Select the letter text and copy it.");
  }
}

function changeSomething(): void {
  out.hidden = true;
  window.scrollTo({ top: 0, behavior: "smooth" });
  byId("cname").focus();
}

function updateIfShowing(): void {
  if (!out.hidden) render(false);
}

for (const grade of GRADES) gradeSelect.appendChild(h("option", {}, grade));

byId("go").addEventListener("click", () => render(true));
byId("demo").addEventListener("click", fillExample);
byId("print").addEventListener("click", () => window.print());
byId("copy").addEventListener("click", () => void copyLetter());
byId("edit").addEventListener("click", changeSomething);
form.addEventListener("input", updateIfShowing);
form.addEventListener("change", updateIfShowing);
form.addEventListener("submit", (event) => event.preventDefault());
